// frontend/src/components/payment/PaymentShell.js
//
// The shared chrome of /payment/success and /payment/failure (and of their
// skeleton): one centred white slip under AuthLayout's navbar.
//
//   PaymentFrame        the page body - a <section>, never a second <main>
//                       (AuthLayout already renders the one main landmark)
//   PaymentCard         the slip: hero field on top, body, brand footer
//   PaymentHero         medallion + the ONE h1 + subtitle / tagline, on the wash
//   PaymentActions      primary / secondary pills, optional tertiary link
//   PaymentReference    the order reference as an LTR, monospace island
//   PaymentBrandFooter  the outlined wordmark over the outlined motto, on white
//   PaymentAnnouncer    one visually-hidden polite live region (the pending
//                       text only - a resolved state is announced by the
//                       focus move to its h1)
//
// All PURE presentation: no fetch, no context, no router - every state of both
// pages renders from props, so each can be screenshot in isolation.
//
// MOBILE FIRST: a 16px side gutter at 360px, the card top-aligned on phones
// and centred in the available height from `sm` (no 100vh arithmetic - the
// frame grows inside AuthLayout's flex column), capped at 560px.
//
// DIRECTION: the frame repeats AuthLayout's `dir` (the old pages did, and a
// screenshot harness may render it alone). Nothing below tests `isHebrew` for
// layout - flex rows and `text-align: end` resolve off the inherited `dir`.

import React from "react";
import { Box, Typography } from "@mui/material";

import Logo from "../brand/Logo";
import { VISUALLY_HIDDEN_SX } from "../../styles/visuallyHidden";
import {
  BODY_SX,
  CARD_SX,
  HERO_SX,
  PRIMARY_ACTION_SX,
  REFERENCE_CODE_SX,
  REFERENCE_SX,
  SECONDARY_ACTION_SX,
  SUBTITLE_SX,
  TAGLINE_SX,
  TERTIARY_ACTION_SX,
  TITLE_SX,
  TONES,
  BRAND,
  hairlineSx,
} from "./paymentBrand";

// Wordmark height, in px: 28 keeps it at the brand's minimum width (120 px,
// brand/tokens.json usage.minimumSize); the motto line under it. The name
// alone, not the lockup: these routes render under AuthLayout's navbar, which
// already shows the icon.
export const BRAND_WORDMARK_HEIGHT = 28;
export const BRAND_MOTTO_HEIGHT = 12;

/**
 * @param {object} p
 * @param {boolean} p.isHebrew
 * @param {string} [p.labelledBy]  the h1's id - names the section
 * @param {string} [p.testId]
 * @param {React.ReactNode} p.children
 */
export function PaymentFrame({ isHebrew, labelledBy, testId, children }) {
  return (
    <Box
      component="section"
      dir={isHebrew ? "rtl" : "ltr"}
      aria-labelledby={labelledBy}
      data-testid={testId}
      sx={{
        position: "relative",
        flex: 1,
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: { xs: "flex-start", sm: "center" },
        px: 2,
        py: { xs: 3, sm: 6 },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * @param {object} p
 * @param {React.ReactNode} p.hero  the hero field's content
 * @param {React.ReactNode} p.children  the body
 * @param {string} [p.state]  `data-state`, for tests and screenshots
 */
export function PaymentCard({ hero, state, children }) {
  return (
    <Box data-testid="payment-card" data-state={state} sx={CARD_SX}>
      <Box sx={HERO_SX}>{hero}</Box>
      <Box sx={BODY_SX}>{children}</Box>
      <PaymentBrandFooter />
    </Box>
  );
}

/**
 * @param {object} p
 * @param {React.ReactNode} p.medallion
 * @param {string} p.titleId
 * @param {React.Ref} [p.titleRef]  the focus target (tabIndex -1)
 * @param {React.ReactNode} p.title
 * @param {React.ReactNode} [p.subtitle]
 * @param {React.ReactNode} [p.tagline]
 * @param {React.ReactNode} [p.taglinePlaceholder]  drawn in the tagline's place
 *        when there is no tagline (the pending state's skeleton line)
 * @param {'brand'|'attention'} [p.tone='brand']  the hairline's tone
 */
export function PaymentHero({
  medallion,
  titleId,
  titleRef,
  title,
  subtitle,
  tagline,
  taglinePlaceholder,
  tone = "brand",
}) {
  return (
    <>
      {medallion}
      <Typography component="h1" id={titleId} ref={titleRef} tabIndex={-1} sx={TITLE_SX}>
        {title}
      </Typography>
      <Box aria-hidden="true" sx={hairlineSx(TONES[tone] || TONES.brand)} />
      {subtitle && (
        <Typography component="p" sx={SUBTITLE_SX}>
          {subtitle}
        </Typography>
      )}
      {tagline ? (
        <Typography component="p" sx={TAGLINE_SX}>
          {tagline}
        </Typography>
      ) : (
        taglinePlaceholder
      )}
    </>
  );
}

/**
 * One action. A `href` renders a link (mailto), otherwise a button.
 *
 * @typedef {object} PaymentAction
 * @property {string} id
 * @property {string} label
 * @property {string} [href]
 * @property {() => void} [onClick]
 */

function ActionControl({ action, sx }) {
  const common = { "data-testid": `payment-action-${action.id}`, sx };
  if (action.href) {
    return (
      <Box component="a" href={action.href} {...common}>
        {action.label}
      </Box>
    );
  }
  return (
    <Box component="button" type="button" onClick={action.onClick} {...common}>
      {action.label}
    </Box>
  );
}

/**
 * The state's actions: the primary pill first (inline-start), the secondary
 * beside it from `sm` and under it on phones, a tertiary link centred below.
 *
 * @param {object} p
 * @param {PaymentAction} p.primary
 * @param {PaymentAction} [p.secondary]
 * @param {PaymentAction} [p.tertiary]
 */
export function PaymentActions({ primary, secondary, tertiary }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.25,
          "& > *": { flex: { sm: "1 1 0" }, width: { xs: "100%", sm: "auto" } },
        }}
      >
        <ActionControl action={primary} sx={PRIMARY_ACTION_SX} />
        {secondary && <ActionControl action={secondary} sx={SECONDARY_ACTION_SX} />}
      </Box>
      {tertiary && (
        <Box sx={{ mt: 1.25, display: "flex", justifyContent: "center" }}>
          <ActionControl action={tertiary} sx={TERTIARY_ACTION_SX} />
        </Box>
      )}
    </Box>
  );
}

/**
 * "<label>: <id>" with the id as an isolated left-to-right run, so a
 * reference like `ORDER-1730-ab12` never reorders inside a Hebrew line.
 *
 * @param {object} p
 * @param {string} p.label
 * @param {string|null} p.orderId  already validated (sanitizeOrderId)
 */
export function PaymentReference({ label, orderId }) {
  if (!orderId) return null;
  return (
    <Typography component="p" sx={REFERENCE_SX} data-testid="payment-reference">
      {label}:{" "}
      <Box component="bdi" dir="ltr" data-testid="payment-reference-id" sx={REFERENCE_CODE_SX}>
        {orderId}
      </Box>
    </Typography>
  );
}

/** The brand mark that signs the slip - images, LTR by construction. */
export function PaymentBrandFooter() {
  return (
    <Box
      sx={{
        borderTop: `1px solid ${BRAND.hairline}`,
        py: { xs: 2.25, sm: 2.5 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
        <Logo variant="wordmark" height={BRAND_WORDMARK_HEIGHT} />
        <Logo variant="motto" height={BRAND_MOTTO_HEIGHT} />
      </Box>
    </Box>
  );
}

// Pinned in the frame's top-left corner: left at its static position, the 1px
// box's -1px margin sits on the inline-start edge (the RIGHT edge in RTL) and
// pushes one pixel past the viewport - the horizontal scroll
// components/loading/SkeletonStatus.js measured and fixed the same way.
const ANNOUNCER_SX = { ...VISUALLY_HIDDEN_SX, top: 0, left: 0, margin: 0 };

/**
 * The page's ONE polite live region. Mounted from the first paint (a region
 * inserted together with its text is not reliably announced), its text
 * changes as the state does; empty renders an empty region.
 *
 * @param {object} p
 * @param {string} [p.message]
 * @param {boolean} p.isHebrew
 */
export function PaymentAnnouncer({ message, isHebrew }) {
  return (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      lang={isHebrew ? "he" : "en"}
      data-testid="payment-announcer"
      sx={ANNOUNCER_SX}
    >
      {message || ""}
    </Box>
  );
}
